import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { captureException } from "@sentry/react";
import { toast } from "react-toastify";
import { useAppData } from "store/appData";
import {
  getImageAnnotations,
  type DatasetImage,
  type ImageAnnotation,
  type DatasetKind,
  type DefectStatus,
  DATASET_API,
} from "../api";
import t from "../translations";

export type DatasetViewMode = "image" | "crops";

interface DatasetDetailContextValue {
  datasetId: number;
  kind: DatasetKind;
  detailBasePath: string;
  datasetName: string;
  // Object type ids included in this dataset (DatasetMeta.type_list).
  datasetTypeList: number[];
  totalImageCount: number;
  projectCounts: { id: number; count: number }[];
  images: DatasetImage[];
  selectedImage: DatasetImage | null;
  annotations: ImageAnnotation[];
  // Re-fetch the open image's annotations (e.g. after editing them). The
  // annotations endpoint is uncached, so this reflects writes immediately.
  refreshAnnotations: () => Promise<void>;
  unsuperverify: (
    region: string,
    projectId: number,
    datasetId: number,
    imageId: number,
  ) => Promise<number>;
  loading: boolean;
  // What occupies the body next to the image list: per-image detail panels
  // ("image", default) or the dataset-wide crops grid ("crops").
  viewMode: DatasetViewMode;
  setViewMode: (mode: DatasetViewMode) => void;
  // Project ids whose accordion is open in the image list. Lifted out of
  // DatasetImageList so other views (e.g. the all-crops grid) can open a
  // project on navigation.
  expandedProjects: Set<number>;
  expandProject: (projectId: number) => void;
  toggleProject: (projectId: number) => void;
  // True after the user explicitly closes the open image. Suppresses the
  // image list's auto-select-first-image effect until the user picks a row.
  imageClosed: boolean;
  setImageClosed: (closed: boolean) => void;
  // Returns true when an image counts as a "positive" for filter purposes.
  // The definition differs by dataset kind — see DATASET_API.hasPositive.
  hasPositive: (image: DatasetImage) => boolean;
  // Colour (and, for defect datasets, the defect-status key) an annotation
  // box should be rendered with — see DATASET_API.getAnnotationColor.
  getAnnotationColor: (annotation: ImageAnnotation) => {
    color: string;
    defectStatus?: DefectStatus;
  };
  // Image ids the user has opened during this dataset-detail session. Local
  // to the provider, so leaving the dataset (provider unmounts) resets it.
  seenImageIds: Set<number>;
  markImageSeen: (imageId: number) => void;
}

const DatasetDetailContext = createContext<DatasetDetailContextValue | null>(
  null,
);

export function DatasetDetailProvider({
  datasetId,
  imageId,
  kind = "object",
  children,
}: {
  datasetId: number;
  imageId: number;
  kind?: DatasetKind;
  children: ReactNode;
}) {
  const [datasetName, setDatasetName] = useState("");
  const [datasetTypeList, setDatasetTypeList] = useState<number[]>([]);
  const [totalImageCount, setTotalImageCount] = useState(0);
  const [projectCounts, setProjectCounts] = useState<
    { id: number; count: number }[]
  >([]);
  const [images, setImages] = useState<DatasetImage[]>([]);
  const [annotations, setAnnotations] = useState<ImageAnnotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<DatasetViewMode>("image");
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
    () => new Set(),
  );
  const [imageClosed, setImageClosed] = useState(false);
  const [seenImageIds, setSeenImageIds] = useState<Set<number>>(
    () => new Set(),
  );

  const markImageSeen = useCallback((imageId: number) => {
    if (imageId < 0) return;
    setSeenImageIds((prev) => {
      if (prev.has(imageId)) return prev;
      const next = new Set(prev);
      next.add(imageId);
      return next;
    });
  }, []);

  const expandProject = useCallback((projectId: number) => {
    setExpandedProjects((prev) => {
      if (prev.has(projectId)) return prev;
      const next = new Set(prev);
      next.add(projectId);
      return next;
    });
  }, []);

  const toggleProject = useCallback((projectId: number) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  }, []);
  const loadControllerRef = useRef<AbortController | null>(null);
  const annotationsControllerRef = useRef<AbortController | null>(null);

  const selectedImage = useMemo(
    () => images.find((img) => img.id === imageId) ?? null,
    [images, imageId],
  );

  const api = DATASET_API[kind];
  const { objectTypes } = useAppData();
  const hasPositive = useCallback(
    (image: DatasetImage) => api.hasPositive(image, objectTypes),
    [api, objectTypes],
  );
  const getAnnotationColor = useCallback(
    (annotation: ImageAnnotation) =>
      api.getAnnotationColor(annotation, objectTypes),
    [api, objectTypes],
  );

  const load = useCallback(async () => {
    loadControllerRef.current?.abort();
    const controller = new AbortController();
    loadControllerRef.current = controller;

    setLoading(true);
    setImages([]);
    try {
      const dataset = await api.detail(datasetId);
      if (controller.signal.aborted) return;

      setDatasetName(dataset.name);
      setDatasetTypeList(dataset.type_list);
      setTotalImageCount(dataset.image_count);
      setProjectCounts(
        dataset.project_counts.map(({ project_id, count }) => ({
          id: project_id,
          count,
        })),
      );

      // Fire one stream per unique region in parallel — regions are known from
      // the detail response so no separate images prefetch is needed.
      const regions = [
        ...new Set(dataset.project_counts.map((pc) => pc.region)),
      ];
      await Promise.all(
        regions.map(async (region) => {
          const batch: DatasetImage[] = [];
          try {
            await api.streamImages(
              datasetId,
              region,
              (image) => {
                batch.push(image);
              },
              controller.signal,
            );
          } catch (error) {
            if (!(
              error instanceof DOMException && error.name === "AbortError"
            )) {
              captureException(error, {
                tags: { feature: "datasetDetail.images" },
              });
            }
          }
          if (!controller.signal.aborted && batch.length > 0) {
            setImages((prev) => [...prev, ...batch]);
          }
        }),
      );
    } catch (error) {
      if (!axios.isCancel(error)) {
        captureException(error, {
          tags: { feature: "datasetDetail.fetch" },
        });
        toast.error(t.FailedToLoadDataset);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [api, datasetId]);

  useEffect(() => {
    if (!datasetId) return;
    load();
    return () => {
      loadControllerRef.current?.abort();
    };
  }, [datasetId, load]);

  const loadAnnotations = useCallback(async () => {
    annotationsControllerRef.current?.abort();
    if (!selectedImage) {
      setAnnotations([]);
      return;
    }

    const controller = new AbortController();
    annotationsControllerRef.current = controller;

    try {
      const data = await getImageAnnotations(
        selectedImage.region,
        selectedImage.project_id,
        selectedImage.id,
        controller.signal,
      );
      setAnnotations(data);
    } catch (error) {
      if (!axios.isCancel(error)) {
        captureException(error, {
          tags: { feature: "datasetDetail.fetch" },
        });
        toast.error(t.FailedToLoadAnnotations);
      }
    }
  }, [selectedImage]);

  useEffect(() => {
    setAnnotations([]);
    loadAnnotations();
    return () => annotationsControllerRef.current?.abort();
  }, [loadAnnotations]);

  return (
    <DatasetDetailContext.Provider
      value={{
        datasetId,
        kind,
        detailBasePath: api.basePath,
        datasetName,
        datasetTypeList,
        totalImageCount,
        projectCounts,
        images,
        selectedImage,
        annotations,
        refreshAnnotations: loadAnnotations,
        unsuperverify: api.unsuperverify,
        loading,
        viewMode,
        setViewMode,
        expandedProjects,
        expandProject,
        toggleProject,
        imageClosed,
        setImageClosed,
        seenImageIds,
        markImageSeen,
        hasPositive,
        getAnnotationColor,
      }}
    >
      {children}
    </DatasetDetailContext.Provider>
  );
}

export function useDatasetDetail(): DatasetDetailContextValue {
  const ctx = useContext(DatasetDetailContext);
  if (!ctx) {
    throw new Error(
      "useDatasetDetail must be used within DatasetDetailProvider",
    );
  }
  return ctx;
}
